import { LitElement, html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-select.styles';
import { SDSDelegateInteractionMixin, SDSEventDispatcherMixin } from '../mixins';

import '../sds-alert/sds-alert';
import '../sds-icon/sds-icon';
import { SDS_SIZES } from '../types';

type SelectSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;

export interface SDSSelectProps {
	'error-message'?: string;
	'help-text'?: string;
	disabled?: boolean;
	label?: string;
	name?: string;
	required?: boolean;
	readonly?: boolean;
	size?: SelectSize;
	value?: string;
	'default-value'?: string;
}

@guardedCustomElement('sds-select')
export class SDSSelect extends SDSDelegateInteractionMixin(SDSEventDispatcherMixin(LitElement)) {
	// Styling
	static override styles = [styles];

	static formAssociated = true;
	public readonly internals: ElementInternals;

	// Props
	@property({ type: String, reflect: true }) name?: SDSSelectProps['name'];
	@property({ type: String, reflect: true }) label?: SDSSelectProps['label'];
	@property({ type: String, attribute: false }) value?: SDSSelectProps['value'];
	@property({ type: String, attribute: 'default-value' }) defaultValue?: SDSSelectProps['default-value'];
	@property({ type: String, attribute: 'help-text', reflect: true }) helpText?: SDSSelectProps['help-text'];
	@property({ type: String, attribute: 'error-message', reflect: true }) errorMessage?: SDSSelectProps['error-message'];
	@property({ type: Boolean, reflect: true }) disabled: SDSSelectProps['disabled'] = false;
	@property({ type: Boolean, reflect: true }) required: SDSSelectProps['required'] = false;
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: SDSSelectProps['readonly'] = false;
	@property({ type: String, reflect: true }) size: SDSSelectProps['size'] = 'md';

	@query('#control') private control!: HTMLSelectElement;

	private _defaultValue: string | null = null;

	// Observe in-place mutations to existing <option>/<optgroup> (text/attrs)
	private _lightDomObserver?: MutationObserver;
	private _pendingLightDomSync = false;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override connectedCallback(): void {
		super.connectedCallback();

		if (this.value == null) {
			const selected = Array.from(this.querySelectorAll('option')).find((option) => option.hasAttribute('selected') || option.defaultSelected);

			if (selected) {
				this.value = selected.value;
			}
		}

		this._startObservingLightDom();
	}

	override disconnectedCallback(): void {
		this._stopObservingLightDom();
		super.disconnectedCallback();
	}

	override firstUpdated() {
		// Let the native <select> decide first (covers <option selected>)
		const controlValue = this.control?.value ?? '';

		// Resolution order:
		// 1. value (controlled)
		// 2. default-value
		// 3. native control value
		this.value = this.value ?? this.defaultValue ?? controlValue;

		// Sync native control
		this.control.value = this.value ?? '';

		// Store default for form reset
		this._defaultValue = this.value ?? null;

		this.internals.setFormValue(this._defaultValue);
		this.updateValidity();
	}

	override updated(changed: Map<PropertyKey, unknown>) {
		if (!this.control) return;

		if (changed.has('value')) {
			this.control.value = this.value ?? '';
			this._setFormValue(this.value);
			this.updateValidity();
		}
	}

	private _setFormValue(value: string | undefined) {
		this.internals.setFormValue(value == null ? null : value);
	}

	private _scheduleLightDomSync() {
		if (this._pendingLightDomSync) return;
		this._pendingLightDomSync = true;

		queueMicrotask(() => {
			this._pendingLightDomSync = false;

			// If light DOM indicates a selected option, treat it as source of truth.
			const selectedFromLightDom = this.getLightDomSelectedValue();
			if (selectedFromLightDom !== undefined && selectedFromLightDom !== this.value) {
				this.value = selectedFromLightDom;
			}

			this.requestUpdate();

			this.updateComplete.then(() => {
				if (!this.control) return;

				this.control.value = this.value ?? '';

				this._setFormValue(this.value);
				this.updateValidity();
			});
		});
	}

	private _startObservingLightDom() {
		if (this._lightDomObserver) return;

		this._lightDomObserver = new MutationObserver((mutations) => {
			for (const m of mutations) {
				if (m.type === 'childList') {
					this._scheduleLightDomSync();
					return;
				}

				if (m.type === 'characterData') {
					this._scheduleLightDomSync();
					return;
				}

				if (m.type === 'attributes') {
					const target = m.target;
					if (!(target instanceof HTMLElement)) continue;

					const tag = target.tagName;
					if (tag === 'OPTION' || tag === 'OPTGROUP' || tag === 'HR') {
						this._scheduleLightDomSync();
						return;
					}
				}
			}
		});

		this._lightDomObserver.observe(this, {
			subtree: true,
			childList: true,
			attributes: true,
			characterData: true,
		});
	}

	private _stopObservingLightDom() {
		this._lightDomObserver?.disconnect();
		this._lightDomObserver = undefined;
		this._pendingLightDomSync = false;
	}

	/**
	 * Called whenever light DOM children change (options/optgroups/hr added/removed).
	 * slotchange won't fire for in-place mutations, so we also use a MutationObserver.
	 */
	private readonly _handleSlotChange = () => {
		if (this.value == null) {
			const selectedValue = this.getLightDomSelectedValue();
			if (selectedValue != null) {
				this.value = selectedValue;
			}
		}

		this._scheduleLightDomSync();
	};

	private updateValidity() {
		if (!this.control) return;

		if (this.control.checkValidity()) {
			this.internals.setValidity({});
		} else {
			this.internals.setValidity({ customError: true }, this.control.validationMessage, this.control);
		}
	}

	private getLightDomSelectedValue(): string | undefined {
		// First selected option wins (matches typical native parsing behavior for single-select)
		const selected = Array.from(this.querySelectorAll('option')).find((option) => option.hasAttribute('selected') || option.defaultSelected);
		return selected?.value;
	}

	// Form-associated lifecycle
	formResetCallback() {
		this.internals.setFormValue(this._defaultValue);

		const newValue = this._defaultValue ?? '';
		if (this.control) {
			this.control.value = newValue;
		}
		this.value = this._defaultValue ?? '';

		this.updateValidity();
	}

	// Event handlers
	private readonly _handleChange = (event: Event) => {
		event.stopImmediatePropagation();

		if (!(event.target instanceof HTMLSelectElement)) return;

		if (this.disabled || this.readOnly) {
			event.target.value = this.value ?? '';
			return;
		}

		this.value = event.target.value;

		this._setFormValue(this.value);
		this.updateValidity();

		this.dispatchSheriffEvent('input');
		this.dispatchSheriffEvent('change');
	};

	private readonly _handleFocus = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.dispatchSheriffEvent('focus');
	};

	private readonly _handleBlur = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.updateValidity();
		this.dispatchSheriffEvent('blur');
	};

	private readonly _handleKeyDown = (event: KeyboardEvent) => {
		if (!this.readOnly) return;

		const keysThatChange = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' ', 'Enter']);

		if (keysThatChange.has(event.key)) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}
	};

	// Rendering helpers: mirror light DOM structure into the internal <select>
	private renderFromLightDom() {
		const nodes = this.children ?? [];
		const children: HTMLElement[] = Array.from(nodes).filter((node): node is HTMLElement => node instanceof HTMLElement);

		return children.map((node) => {
			switch (node.tagName) {
				case 'OPTION':
					return this.renderOption(node as HTMLOptionElement);
				case 'OPTGROUP':
					return this.renderOptgroup(node as HTMLOptGroupElement);
				case 'HR':
					return html`<hr />`;
				default:
					return nothing;
			}
		});
	}

	private renderOption(option: HTMLOptionElement) {
		const attrValue = option.getAttribute('value');
		const optionValueForSelect = option.value;
		const isSelected = this.value != null && optionValueForSelect === this.value;

		return html` <option value=${ifDefined(attrValue === null ? undefined : attrValue)} ?disabled=${option.disabled} .selected=${isSelected}>${option.text}</option> `;
	}

	private renderOptgroup(group: HTMLOptGroupElement) {
		const options = Array.from(group.querySelectorAll('option'));

		return html` <optgroup label=${ifDefined(group.label || undefined)} ?disabled=${group.disabled}>${options.map((opt) => this.renderOption(opt))}</optgroup> `;
	}

	// Render
	// Hidden slot: holds light DOM <option>/<optgroup>/<hr> and triggers slotchange
	override render() {
		const selectClasses = {
			'sds-select': true,
			'sds-select--error': Boolean(this.errorMessage),
		};

		return html`
			<div class="${classMap(selectClasses)}">
				${this.label
					? html` <label for="control" class="sds-select__label"> ${this.readOnly ? html`<sds-icon name="lock-material-filled" size="m"></sds-icon>` : nothing} ${this.label} </label> `
					: nothing}
				${this.helpText ? html`<p class="sds-select__help-text">${this.helpText}</p>` : nothing}

				<div hidden>
					<slot @slotchange=${this._handleSlotChange}></slot>
				</div>

				<select
					id="control"
					class="sds-select__control"
					name=${ifDefined(this.name)}
					?disabled=${this.disabled}
					?required=${this.required}
					aria-readonly=${ifDefined(this.readOnly)}
					aria-invalid=${this.errorMessage ? 'true' : 'false'}
					@keydown=${this._handleKeyDown}
					@change=${this._handleChange}
					@focus=${this._handleFocus}
					@blur=${this._handleBlur}
				>
					${this.renderFromLightDom()}
				</select>

				${this.errorMessage
					? html`
							<sds-alert variant="secondary" data-palette="danger">
								<p slot="message">${this.errorMessage}</p>
							</sds-alert>
						`
					: nothing}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-select': SDSSelect;
	}
}
